import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { WebhookStatusEnum } from 'lane-shared/helpers/webhooks/WebhookType';

import { useChannelWebhookSummaries } from 'components/webhooks/useChannelWebhookSummaries';

type DuplicateUrlCheckResult = {
  showActiveError: boolean;
  activeErrorText: string | null;
  showInactiveWarning: boolean;
  inactiveWarningText: string | null;
};

type WebhookSummary = {
  _id: string;
  name: string;
  url: string;
  status: string;
};

const EMPTY_RESULT: DuplicateUrlCheckResult = {
  showActiveError: false,
  activeErrorText: null,
  showInactiveWarning: false,
  inactiveWarningText: null,
};

/**
 * Normalizes a URL for comparison by trimming whitespace and trailing slashes.
 * e.g. "https://example.com/hook/ " → "https://example.com/hook"
 */
function normalizeUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function getWarningText(
  t: (key: string, options?: Record<string, string>) => string,
  willBeActive: boolean,
  matches: WebhookSummary[]
): string | null {
  const activeMatch = matches.find(s => s.status === WebhookStatusEnum.Live);
  const inactiveMatch = matches.find(
    s => s.status === WebhookStatusEnum.Disabled
  );

  if (!willBeActive && activeMatch) {
    return t('web.admin.webhooks.url.warningMessage', {
      webhookName: activeMatch.name,
    });
  }

  const match = inactiveMatch ?? matches[0];

  return match
    ? t('web.admin.webhooks.url.warningMessage', {
        webhookName: match.name,
      })
    : null;
}

function computeDuplicateState(
  t: (key: string, options?: Record<string, string>) => string,
  matches: WebhookSummary[],
  intendedStatus: string
): DuplicateUrlCheckResult {
  const activeMatch = matches.find(s => s.status === WebhookStatusEnum.Live);
  const willBeActive = intendedStatus === WebhookStatusEnum.Live;
  const hasActiveConflict = willBeActive && !!activeMatch;

  const warningText = hasActiveConflict
    ? null
    : getWarningText(t, willBeActive, matches);

  return {
    showActiveError: hasActiveConflict,
    activeErrorText: activeMatch
      ? t('web.admin.webhooks.url.errorMessage', {
          webhookName: activeMatch.name,
        })
      : null,
    showInactiveWarning: !hasActiveConflict && !!warningText,
    inactiveWarningText: warningText,
  };
}

/**
 * Encapsulates the duplicate-URL check logic for webhook Create/Edit forms.
 *
 * Rules:
 * - Will be Active + same URL already Active → error, block save
 * - Will be Inactive + same URL exists (Active or Inactive) → warning, allow
 * - Will be Active + same URL only Inactive → warning, allow
 *
 * URL comparison ignores trailing slashes and whitespace.
 * Gated by `isEnabled` (feature flag). When disabled, returns no-ops.
 */
export function useDuplicateUrlCheck({
  channelId,
  currentUrl,
  intendedStatus,
  excludeWebhookId,
  isEnabled,
}: {
  channelId: string | undefined;
  currentUrl: string;
  intendedStatus: string;
  excludeWebhookId?: string;
  isEnabled: boolean;
}): DuplicateUrlCheckResult {
  const { t } = useTranslation();
  const summaries = useChannelWebhookSummaries(
    isEnabled ? channelId : undefined
  );

  return useMemo(() => {
    if (!isEnabled) {
      return EMPTY_RESULT;
    }

    const normalizedCurrentUrl = normalizeUrl(currentUrl);

    const matches = summaries.filter(
      s =>
        normalizeUrl(s.url) === normalizedCurrentUrl &&
        s._id !== excludeWebhookId
    );

    if (matches.length === 0) {
      return EMPTY_RESULT;
    }

    return computeDuplicateState(t, matches, intendedStatus);
  }, [summaries, currentUrl, intendedStatus, excludeWebhookId, isEnabled, t]);
}
